import firebase from 'firebase/app';
import { AuthStatus, HasuraClaimKey, HasuraClaims } from 'constants/auth';
import { getDashboardSubscriptionName } from 'constants/payments';
import { Viewer } from 'constants/user';
import { toPostgresArray } from 'utils/shared/hasura';
import { DEV_VIEWER_UID, DevViewerTier, parseDevViewerTier } from 'utils/shared/devViewerTier';

const DEV_VIEWER_EMAIL = 'dev-viewer-override@localhost.invalid';
const OPRA_AGREEMENT_END = '9999-12-31T23:59:59.999Z';
const rejectAccountMutation = async (): Promise<never> => {
  throw new Error('The development viewer cannot mutate an account');
};

export const getDevViewerTier = (): DevViewerTier | null => {
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  return parseDevViewerTier(process.env.NEXT_PUBLIC_DEV_VIEWER_TIER);
};

const createClaims = (tier: Exclude<DevViewerTier, 'anonymous'>): HasuraClaims => ({
  [HasuraClaimKey.AllowedRoles]: ['user'],
  [HasuraClaimKey.DefaultRole]: 'user',
  [HasuraClaimKey.UserID]: DEV_VIEWER_UID,
  [HasuraClaimKey.Subscriptions]: toPostgresArray(
    tier === 'pro' ? [getDashboardSubscriptionName()] : [],
  ),
  [HasuraClaimKey.OpraAgreement]: tier === 'pro' ? OPRA_AGREEMENT_END : '',
  [HasuraClaimKey.LaunchAccess]: '',
});

const createFirebaseUser = (): firebase.User =>
  ({
    displayName: 'Development viewer',
    email: DEV_VIEWER_EMAIL,
    emailVerified: true,
    isAnonymous: false,
    metadata: {},
    phoneNumber: null,
    photoURL: null,
    providerData: [],
    providerId: 'dev-viewer-override',
    refreshToken: '',
    tenantId: null,
    uid: DEV_VIEWER_UID,
    delete: rejectAccountMutation,
    getIdToken: async () => '',
    getIdTokenResult: async () => ({
      claims: {},
      token: '',
      authTime: '',
      issuedAtTime: '',
      expirationTime: '',
      signInProvider: null,
      signInSecondFactor: null,
    }),
    linkAndRetrieveDataWithCredential: rejectAccountMutation,
    linkWithCredential: rejectAccountMutation,
    linkWithPhoneNumber: rejectAccountMutation,
    linkWithPopup: rejectAccountMutation,
    linkWithRedirect: rejectAccountMutation,
    reauthenticateAndRetrieveDataWithCredential: rejectAccountMutation,
    reauthenticateWithCredential: rejectAccountMutation,
    reauthenticateWithPhoneNumber: rejectAccountMutation,
    reauthenticateWithPopup: rejectAccountMutation,
    reauthenticateWithRedirect: rejectAccountMutation,
    reload: async () => undefined,
    sendEmailVerification: rejectAccountMutation,
    toJSON: () => ({ uid: DEV_VIEWER_UID, email: DEV_VIEWER_EMAIL }),
    unlink: rejectAccountMutation,
    updateEmail: rejectAccountMutation,
    updatePassword: rejectAccountMutation,
    updatePhoneNumber: rejectAccountMutation,
    updateProfile: rejectAccountMutation,
    verifyBeforeUpdateEmail: rejectAccountMutation,
  } as unknown as firebase.User);

export const applyDevViewerOverride = (viewer: Viewer): Viewer => {
  const tier = getDevViewerTier();

  if (!tier || viewer.status === AuthStatus.Loading) {
    return viewer;
  }

  if (tier === 'anonymous') {
    return {
      status: AuthStatus.Anonymous,
      viewer: null,
      config: null,
      claims: null,
    };
  }

  return {
    status: AuthStatus.User,
    viewer: createFirebaseUser(),
    config: null,
    claims: createClaims(tier),
  };
};
